/**
 * Header — sticky global navigation header.
 *
 * Transparent at the top of the page, transitions to a
 * solid surface with a subtle border and backdrop blur on
 * scroll. Contains the logo, desktop navigation items,
 * and an auth-aware right section:
 *
 * - Guest:     Login link + Apply CTA
 * - Authed:    Notification bell + avatar dropdown + status-based CTA
 *
 * The CTA adapts to the user's creator profile status:
 *   Apply → Continue → View → Under Review → Dashboard → Resubmit
 */

import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Container } from '@/components/ui';
import { NAV_ITEMS } from '@/config/navigation';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useAuth } from '@/hooks/auth';
import { useCreatorProfile } from '@/hooks/useCreatorProfile';
import { analyticsService } from '@/lib/AnalyticsService';
import { MobileNav } from './MobileNav';
import { UserMenu } from './UserMenu';
import { BrandLogo } from './BrandLogo';
import { NotificationCenter } from '@/components/notification/NotificationCenter';

export function Header(): JSX.Element {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isScrolled = useScrollPosition(8);
  const { user } = useAuth();
  const { action } = useCreatorProfile();

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'text-sm font-medium tracking-tight transition-colors duration-fast ease-standard',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:rounded',
      isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
    );

  const cta = action ?? { label: 'Apply', path: '/apply' };

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-40 transition-all duration-normal ease-standard',
          isScrolled
            ? 'border-b border-border bg-surface/80 backdrop-blur-[2px]'
            : 'border-b border-transparent bg-transparent',
        )}
      >
        <Container maxWidth="2xl">
          <div className="flex h-[72px] items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="UNIVIBE home"
            >
              <BrandLogo className="h-3 w-auto shrink-0 lg:h-4" />
            </Link>

            {/* Desktop navigation */}
            <nav className="hidden lg:flex lg:items-center lg:gap-8" aria-label="Primary">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.path} to={item.path} className={navLinkClass}>
                  {item.label}
                </NavLink>
              ))}

              {user ? (
                <>
                  <NotificationCenter />
                  <UserMenu />
                </>
              ) : (
                <NavLink to="/login" className={navLinkClass}>
                  Login
                </NavLink>
              )}

              {/* Status-based CTA */}
              <Link
                to={cta.path}
                onClick={() => analyticsService.track('registration_cta_clicked', { location: 'header' })}
                className={cn(
                  'inline-flex h-8 items-center justify-center rounded-md px-4 text-xs font-medium whitespace-nowrap',
                  'bg-primary-700 text-primary-foreground',
                  'transition-all duration-fast ease-standard',
                  'hover:bg-primary-800 active:bg-primary-900',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                )}
              >
                {cta.label}
              </Link>
            </nav>

            {/* Mobile menu trigger */}
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className={cn(
                'inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground',
                'transition-colors duration-fast ease-standard hover:bg-neutral-100',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'lg:hidden',
              )}
              aria-label="Open navigation menu"
              aria-expanded={mobileNavOpen}
              aria-controls={mobileNavOpen ? 'mobile-nav' : undefined}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </Container>
      </header>

      <MobileNav isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
    </>
  );
}
